import * as pdfjsLib from "pdfjs-dist";

// Ensure PDF.js worker is set up
if (typeof window !== "undefined" && !pdfjsLib.GlobalWorkerOptions.workerSrc) {
  pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf/pdf.worker.min.js";
}

export interface PdfDimensions {
  width: number;
  height: number;
  totalPages: number;
}

/**
 * Extract dimensions and page count from a PDF file
 * @param file The PDF file to analyze
 * @returns Promise containing width, height (from first page) and total pages
 */
export async function extractPdfDimensions(file: File): Promise<PdfDimensions> {
  try {
    // Convert file to ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();

    // Load the PDF document
    const loadingTask = pdfjsLib.getDocument({
      data: arrayBuffer,
      // Disable range requests since we're loading from memory
      disableRange: true,
      disableStream: true,
    });

    const pdfDocument = await loadingTask.promise;

    // Get the first page to extract dimensions
    const firstPage = await pdfDocument.getPage(1);
    const viewport = firstPage.getViewport({ scale: 1 });

    // Clean up resources
    await pdfDocument.destroy();

    return {
      width: Math.round(viewport.width),
      height: Math.round(viewport.height),
      totalPages: pdfDocument.numPages,
    };
  } catch (error) {
    console.error("Error extracting PDF dimensions:", error);
    // Return default dimensions if extraction fails
    return {
      width: 400,
      height: 600,
      totalPages: 1,
    };
  }
}

/**
 * Calculate appropriate display dimensions for a PDF while maintaining aspect ratio
 * @param originalWidth Original PDF width
 * @param originalHeight Original PDF height
 * @param maxWidth Maximum allowed width (default: 800)
 * @param maxHeight Maximum allowed height (default: 1000)
 * @returns Scaled dimensions that fit within the max bounds
 */
export function calculatePdfDisplayDimensions(
  originalWidth: number,
  originalHeight: number,
  maxWidth: number = 800,
  maxHeight: number = 1000
): { width: number; height: number } {
  const aspectRatio = originalWidth / originalHeight;

  let width = originalWidth;
  let height = originalHeight;

  // Scale down if dimensions exceed maximums
  if (width > maxWidth) {
    width = maxWidth;
    height = width / aspectRatio;
  }

  if (height > maxHeight) {
    height = maxHeight;
    width = height * aspectRatio;
  }

  // Ensure minimum dimensions
  const minWidth = 200;
  const minHeight = 200;

  if (width < minWidth) {
    width = minWidth;
    height = width / aspectRatio;
  }

  if (height < minHeight) {
    height = minHeight;
    width = height * aspectRatio;
  }

  return {
    width: Math.round(width),
    height: Math.round(height),
  };
}
